import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Card from '../../components/common/Card/Card';
import Button from '../../components/common/Button/Button';
import Table from '../../components/common/Table/Table';
import Modal from '../../components/common/Modal/Modal';
import StatusBadge from '../../components/common/StatusBadge/StatusBadge';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import SearchBar from '../../components/common/SearchBar/SearchBar';
import SearchableSelect from '../../components/common/SearchableSelect/SearchableSelect';
import { getAllProjects, createProject, deleteProject, updateProjectStatus } from '../../services/projectService';
import { getAllMentors } from '../../services/mentorService';
import { getAllStudents } from '../../services/studentService';
import { getAllCounsellors, assignCounsellorToProject } from '../../services/counsellorService';
import { formatDate } from '../../utils/helpers';
import useForm from '../../hooks/useForm';
import useAuth from '../../hooks/useAuth';
import { useToast, useConfirm } from '../../context/ToastContext';
import styles from './Projects.module.css';

const Projects = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const baseRoute = location.pathname.includes('/super-admin/') ? '/super-admin' : '/manager';
  const [projects, setProjects] = useState([]);
  const [mentors, setMentors] = useState([]);
  const [students, setStudents] = useState([]);
  const [counsellors, setCounsellors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const toast = useToast();
  const confirm = useConfirm();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [projectsData, mentorsData, studentsData, counsellorsData] = await Promise.all([
        getAllProjects(),
        getAllMentors(),
        getAllStudents(),
        getAllCounsellors()
      ]);
      setProjects(projectsData);
      setMentors(mentorsData);
      setStudents(studentsData);
      setCounsellors(counsellorsData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const { values, handleChange, handleSubmit, resetForm , isSubmitting} = useForm(
    {
      mentor_id: '',
      student_id: '',
      project_name: '',
      project_description: '',
      project_track: 'product',
      start_date: '',
      end_date: '',
      counsellor_id: ''
    },
    async (formValues) => {
      try {
        // Remove counsellor_id from project creation data
        const { counsellor_id, ...projectData } = formValues;
        
        // Create the project
        const newProject = await createProject(projectData);
        
        // If counsellor was selected, assign them to the project
        if (counsellor_id && counsellor_id !== '') {
          try {
            await assignCounsellorToProject(newProject.id, counsellor_id);
          } catch (error) {
            console.error('Error assigning counsellor:', error);
            toast.warning('Project created but failed to assign counsellor. You can assign them later.');
          }
        }
        
        setShowModal(false);
        resetForm();
        fetchData();
      } catch (error) {
        toast.error('Error creating project: ' + error.message);
      }
    }
  );

  const handleDelete = async (projectId) => {
    const confirmed = await confirm('Are you sure you want to delete this project? This will delete all related data.', { confirmText: 'Delete Project', variant: 'danger' });
    if (confirmed) {
      try {
        await deleteProject(projectId);
        fetchData();
      } catch (error) {
        toast.error('Error deleting project: ' + error.message);
      }
    }
  };

  const handleStatusChange = async (projectId, newStatus) => {
    try {
      await updateProjectStatus(projectId, newStatus);
      fetchData();
    } catch (error) {
      toast.error('Error updating project status: ' + error.message);
    }
  };

  const getMentorName = (mentorId) => {
    const mentor = mentors.find(m => m.id === mentorId);
    return mentor ? `${mentor.first_name} ${mentor.last_name}` : 'N/A';
  };

  const getStudentName = (studentId) => {
    const student = students.find(s => s.id === studentId);
    return student ? `${student.first_name} ${student.last_name}` : 'N/A';
  };

  // Filter projects based on search query
  const filteredProjects = useMemo(() => {
    if (!searchQuery) return projects;
    
    const query = searchQuery.toLowerCase();
    return projects.filter(project => {
      const projectName = project.project_name?.toLowerCase() || '';
      const mentorName = getMentorName(project.mentor_id).toLowerCase();
      const studentName = getStudentName(project.student_id).toLowerCase();
      
      return projectName.includes(query) || 
             mentorName.includes(query) || 
             studentName.includes(query);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects, searchQuery, mentors, students]);

  const columns = [
    { header: 'Project Name', accessor: 'project_name' },
    { 
      header: 'Mentor', 
      sortKey: 'mentor_id',
      sortValue: (row) => getMentorName(row.mentor_id),
      render: (row) => getMentorName(row.mentor_id) 
    },
    { 
      header: 'Student', 
      sortKey: 'student_id',
      sortValue: (row) => getStudentName(row.student_id),
      render: (row) => getStudentName(row.student_id) 
    },
    { 
      header: 'Track', 
      accessor: 'project_track', 
      render: (row) => row.project_track.replace(/_/g, ' ').toUpperCase() 
    },
    { 
      header: 'Status',
      accessor: 'status',
      render: (row) => <StatusBadge status={row.status} /> 
    },
    { 
      header: 'Start Date', 
      accessor: 'start_date', 
      render: (row) => formatDate(row.start_date) 
    },
    { 
      header: 'End Date', 
      accessor: 'end_date', 
      render: (row) => formatDate(row.end_date) 
    },
    { 
      header: 'Actions', 
      render: (row) => (
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <Button size="small" 
            variant="outline" 
            onClick={() => navigate(`${baseRoute}/projects/${row.id}`)}
          >
            View Details
          </Button>
          {isSuperAdmin && row.status !== 'on_hold' && row.status !== 'cancelled' && (
            <Button size="small" 
              variant="warning" 
              onClick={() => handleStatusChange(row.id, 'on_hold')} loading={loading}
              style={{ backgroundColor: '#FF9800', color: 'white' }}
            >
              On Hold
            </Button>
          )}
          {/* {isSuperAdmin && row.status !== 'cancelled' && (
            <Button size="small" 
              variant="danger" 
              onClick={() => handleStatusChange(row.id, 'cancelled')} loading={loading}
              style={{ backgroundColor: '#F44336' }}
            >
              Cancel
            </Button>
          )} */}
          {isSuperAdmin && (row.status === 'on_hold' || row.status === 'cancelled') && (
            <Button size="small" 
              variant="primary" 
              onClick={() => handleStatusChange(row.id, 'in_progress')} loading={loading}
              style={{ backgroundColor: '#2196F3' }}
            >
              Resume
            </Button>
          )}
          {isSuperAdmin && (
            <Button size="small" variant="danger" onClick={() => handleDelete(row.id)} loading={loading}>
              Delete
            </Button>
          )}
        </div>
      )
    }
  ];

  if (loading) {
    return <div className={styles.loading}><LoadingSpinner /></div>;
  }

  return (
    <div className={styles.projectsPage}>
      <div className={styles.header}>
        <h1>Projects</h1>
        <div className={styles.headerActions}>
          <SearchBar 
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search projects by name, mentor, or student..."
          />
          <Button icon="+" onClick={() => setShowModal(true)} loading={loading}>Create Project</Button>
        </div>
      </div>

      <Card>
        <div className={styles.tableContainer}>
          <Table columns={columns} data={filteredProjects} />
        </div>
      </Card>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="Create New Project" size="large">
        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label>Mentor *</label>
              <SearchableSelect
                name="mentor_id"
                value={values.mentor_id}
                onChange={handleChange}
                options={mentors}
                placeholder="Select Mentor"
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label>Student *</label>
              <SearchableSelect
                name="student_id"
                value={values.student_id}
                onChange={handleChange}
                options={students}
                placeholder="Select Student"
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label>Project Track *</label>
              <select name="project_track" value={values.project_track} onChange={handleChange} required>
                <option value="product">Product</option>
                <option value="research">Research</option>
                <option value="product_research">Product + Research</option>
              </select>
            </div>

            <div className={styles.formGroup}>
              <label>Start Date *</label>
              <input type="date" name="start_date" value={values.start_date} onChange={handleChange} required />
            </div>

            <div className={styles.formGroup}>
              <label>End Date</label>
              <input type="date" name="end_date" value={values.end_date} onChange={handleChange} />
            </div>

            <div className={styles.formGroup}>
              <label>Counsellor (Optional)</label>
              <select name="counsellor_id" value={values.counsellor_id} onChange={handleChange}>
                <option value="">No Counsellor</option>
                {counsellors.map(counsellor => (
                  <option key={counsellor.id} value={counsellor.id}>
                    {counsellor.first_name} {counsellor.last_name} - {counsellor.specialization || 'General'}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className={styles.formGroup}>
            <label>Project Name *</label>
            <input name="project_name" value={values.project_name} onChange={handleChange} required />
          </div>

          <div className={styles.formGroup}>
            <label>Project Description</label>
            <textarea name="project_description" value={values.project_description} onChange={handleChange} rows="3" />
          </div>

          <div className={styles.formActions}>
            <Button type="button" variant="outline" onClick={() => setShowModal(false)} loading={loading}>Cancel</Button>
            <Button type="submit" loading={isSubmitting}>Create Project</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Projects;